import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Wallet
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import advancePaymentService from '../../services/advancePaymentService'
import employeeMasterService from '../../services/employeeMasterService'
import paymentMethodMasterService from '../../services/paymentMethodMasterService'
import '../../styles/expenseClaimForm.css'

const AdvancePaymentForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = location.pathname.includes('/edit')
  const isView = location.pathname.includes('/view')
  const advanceData = location.state?.advanceData

  const [formData, setFormData] = useState({
    advanceNumber: '',
    employee: '',
    employeeName: '',
    employeeCode: '',
    department: '',
    advanceDate: new Date().toISOString().split('T')[0],
    advanceAmount: 0,
    currency: 'INR',
    purpose: '',
    description: '',
    paymentMethod: '',
    paymentMethodName: '',
    paymentDate: '',
    paymentReference: '',
    paymentTransactionId: '',
    bankAccountNumber: '',
    bankName: '',
    ifscCode: '',
    accountHolderName: '',
    status: 'Pending',
    projectCode: '',
    projectName: '',
    clientCode: '',
    clientName: '',
    expectedSettlementDate: '',
    notes: ''
  })

  const [isLoadingAdvanceNumber, setIsLoadingAdvanceNumber] = useState(true)
  const [employees, setEmployees] = useState([])
  const [paymentMethods, setPaymentMethods] = useState([])
  const [loading, setLoading] = useState(false)

  // Load initial data
  useEffect(() => {
    if (!isEdit && !isView) {
      fetchAdvanceNumber()
    } else if (advanceData) {
      // Populate form with existing data
      setFormData({
        advanceNumber: advanceData.advanceNumber || '',
        employee: advanceData.employee?._id || advanceData.employee || '',
        employeeName: advanceData.employeeName || '',
        employeeCode: advanceData.employeeCode || '',
        department: advanceData.department || '',
        advanceDate: advanceData.advanceDate ? new Date(advanceData.advanceDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        advanceAmount: advanceData.advanceAmount || 0,
        currency: advanceData.currency || 'INR',
        purpose: advanceData.purpose || '',
        description: advanceData.description || '',
        paymentMethod: advanceData.paymentMethod?._id || advanceData.paymentMethod || '',
        paymentMethodName: advanceData.paymentMethodName || '',
        paymentDate: advanceData.paymentDate ? new Date(advanceData.paymentDate).toISOString().split('T')[0] : '',
        paymentReference: advanceData.paymentReference || '',
        paymentTransactionId: advanceData.paymentTransactionId || '',
        bankAccountNumber: advanceData.bankAccountNumber || '',
        bankName: advanceData.bankName || '',
        ifscCode: advanceData.ifscCode || '',
        accountHolderName: advanceData.accountHolderName || '',
        status: advanceData.status || 'Pending',
        projectCode: advanceData.projectCode || '',
        projectName: advanceData.projectName || '',
        clientCode: advanceData.clientCode || '',
        clientName: advanceData.clientName || '',
        expectedSettlementDate: advanceData.expectedSettlementDate ? new Date(advanceData.expectedSettlementDate).toISOString().split('T')[0] : '',
        notes: advanceData.notes || ''
      })
      setIsLoadingAdvanceNumber(false)
    }

    // Load dropdown data
    loadDropdownData()
  }, [])

  const fetchAdvanceNumber = async () => {
    try {
      setIsLoadingAdvanceNumber(true)
      const response = await advancePaymentService.getNextAdvanceNumber()
      if (response.success && response.data) {
        setFormData(prev => ({
          ...prev,
          advanceNumber: response.data.advanceNumber || response.data.formattedValue || 'ADV0001'
        }))
      }
    } catch (error) {
      console.error('Error fetching advance number:', error)
      setFormData(prev => ({ ...prev, advanceNumber: 'ADV0001' }))
    } finally {
      setIsLoadingAdvanceNumber(false)
    }
  }

  const loadDropdownData = async () => {
    try {
      const [employeesRes, paymentMethodsRes] = await Promise.all([
        employeeMasterService.getAllEmployees(),
        paymentMethodMasterService.getActivePaymentMethods()
      ])

      if (employeesRes.success) {
        setEmployees(employeesRes.data || [])
      }
      if (paymentMethodsRes.success) {
        setPaymentMethods(paymentMethodsRes.data || [])
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Update employee details when employee is selected
    if (name === 'employee') {
      const selectedEmployee = employees.find(emp => emp._id === value)
      if (selectedEmployee) {
        setFormData(prev => ({
          ...prev,
          employeeName: selectedEmployee.employeeName || '',
          employeeCode: selectedEmployee.employeeCode || '',
          department: selectedEmployee.department || ''
        }))
      }
    }

    // When payment method is selected, populate payment method name
    if (name === 'paymentMethod') {
      const selectedMethod = paymentMethods.find(method => method._id === value)
      if (selectedMethod) {
        setFormData(prev => ({
          ...prev,
          paymentMethodName: selectedMethod.paymentMethodName || ''
        }))
      }
    }
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.employee) {
        toast.error('Please select an employee')
        return
      }
      if (!formData.purpose) {
        toast.error('Please enter the purpose of advance')
        return
      }
      if (!formData.paymentMethod) {
        toast.error('Please select a payment method')
        return
      }
      if (formData.advanceAmount <= 0) {
        toast.error('Advance amount must be greater than 0')
        return
      }

      setLoading(true)
      let response
      if (isEdit) {
        response = await advancePaymentService.updateAdvancePayment(advanceData._id, formData)
      } else {
        response = await advancePaymentService.createAdvancePayment(formData)
      }

      if (response.success) {
        toast.success(isEdit ? 'Advance payment updated successfully!' : 'Advance payment created successfully!')
        navigate('/expenses/advance-payment')
      } else {
        toast.error(response.message || 'Failed to save advance payment')
      }
    } catch (error) {
      console.error('Error saving advance payment:', error)
      if (!error._errorHandled) {
        if (error.response?.status === 400) {
          toast.error(error.response.data?.message || 'Invalid data. Please check your inputs.')
        } else {
          toast.error('Failed to save advance payment. Please try again.')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    if (isEdit || isView) {
      // Reset to original data
      if (advanceData) {
        setFormData({
          advanceNumber: advanceData.advanceNumber || '',
          employee: advanceData.employee?._id || advanceData.employee || '',
          employeeName: advanceData.employeeName || '',
          employeeCode: advanceData.employeeCode || '',
          department: advanceData.department || '',
          advanceDate: advanceData.advanceDate ? new Date(advanceData.advanceDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          advanceAmount: advanceData.advanceAmount || 0,
          currency: advanceData.currency || 'INR',
          purpose: advanceData.purpose || '',
          description: advanceData.description || '',
          paymentMethod: advanceData.paymentMethod?._id || advanceData.paymentMethod || '',
          paymentMethodName: advanceData.paymentMethodName || '',
          paymentDate: advanceData.paymentDate ? new Date(advanceData.paymentDate).toISOString().split('T')[0] : '',
          paymentReference: advanceData.paymentReference || '',
          paymentTransactionId: advanceData.paymentTransactionId || '',
          bankAccountNumber: advanceData.bankAccountNumber || '',
          bankName: advanceData.bankName || '',
          ifscCode: advanceData.ifscCode || '',
          accountHolderName: advanceData.accountHolderName || '',
          status: advanceData.status || 'Pending',
          projectCode: advanceData.projectCode || '',
          projectName: advanceData.projectName || '',
          clientCode: advanceData.clientCode || '',
          clientName: advanceData.clientName || '',
          expectedSettlementDate: advanceData.expectedSettlementDate ? new Date(advanceData.expectedSettlementDate).toISOString().split('T')[0] : '',
          notes: advanceData.notes || ''
        })
      }
    } else {
      // Reset to initial state
      setFormData({
        advanceNumber: formData.advanceNumber, // Keep the advance number
        employee: '',
        employeeName: '',
        employeeCode: '',
        department: '',
        advanceDate: new Date().toISOString().split('T')[0],
        advanceAmount: 0,
        currency: 'INR',
        purpose: '',
        description: '',
        paymentMethod: '',
        paymentMethodName: '',
        paymentDate: '',
        paymentReference: '',
        paymentTransactionId: '',
        bankAccountNumber: '',
        bankName: '',
        ifscCode: '',
        accountHolderName: '',
        status: 'Pending',
        projectCode: '',
        projectName: '',
        clientCode: '',
        clientName: '',
        expectedSettlementDate: '',
        notes: ''
      })
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Advance Payments', path: '/expenses/advance-payment', onClick: () => navigate('/expenses/advance-payment') },
    { label: isEdit ? 'Edit Advance Payment' : isView ? 'View Advance Payment' : 'New Advance Payment', path: location.pathname, isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Advance Payment</h1>
          <p>{isEdit ? 'Edit advance payment' : isView ? 'View advance payment' : 'Create new advance payment'}</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/expenses/advance-payment')}>
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Advance Payment Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>Advance Details</h3>
          </div>

          {/* Row 1: Advance Number, Employee, Advance Date, Currency */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="advanceNumber" className="form-label required">
                Advance Number
              </label>
              <input
                type="text"
                id="advanceNumber"
                name="advanceNumber"
                value={isLoadingAdvanceNumber ? 'Generating...' : formData.advanceNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="employee" className="form-label required">
                Employee
              </label>
              <select
                id="employee"
                name="employee"
                value={formData.employee}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isView || isEdit}
              >
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {emp.employeeName} ({emp.employeeCode})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="advanceDate" className="form-label required">
                Advance Date
              </label>
              <input
                type="date"
                id="advanceDate"
                name="advanceDate"
                value={formData.advanceDate}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="currency" className="form-label required">
                Currency
              </label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isView}
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Employee Details</h3>
            </div>
          </div>

          {/* Row 2: Employee Information (Read-only) */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="employeeName" className="form-label">
                Employee Name
              </label>
              <input
                type="text"
                id="employeeName"
                name="employeeName"
                value={formData.employeeName}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="employeeCode" className="form-label">
                Employee Code
              </label>
              <input
                type="text"
                id="employeeCode"
                name="employeeCode"
                value={formData.employeeCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="department" className="form-label">
                Department
              </label>
              <input
                type="text"
                id="department"
                name="department"
                value={formData.department}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <input
                type="text"
                id="status"
                name="status"
                value={formData.status}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>
          </div>

          {/* Row 3: Purpose, Description, Advance Amount, Expected Settlement Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="purpose" className="form-label required">
                Purpose
              </label>
              <input
                type="text"
                id="purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                className="form-input"
                required
                placeholder="Purpose of advance"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="advanceAmount" className="form-label required">
                Advance Amount
              </label>
              <input
                type="number"
                id="advanceAmount"
                name="advanceAmount"
                value={formData.advanceAmount}
                onChange={handleInputChange}
                className="form-input"
                required
                min="0"
                step="0.01"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="expectedSettlementDate" className="form-label">
                Expected Settlement Date
              </label>
              <input
                type="date"
                id="expectedSettlementDate"
                name="expectedSettlementDate"
                value={formData.expectedSettlementDate}
                onChange={handleInputChange}
                className="form-input"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Additional description"
                disabled={isView}
              />
            </div>
          </div>

          {/* Payment Information Section */}
          <div className="form-section-divider">
            <div>
              <h2 className="section-title">Payment Information</h2>
              <p className="section-subtitle">Payment method and transaction details</p>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="paymentMethod" className="form-label required">
                Payment Method
              </label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isView}
              >
                <option value="">Select Payment Method</option>
                {paymentMethods.map(method => (
                  <option key={method._id} value={method._id}>
                    {method.paymentMethodName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="paymentDate" className="form-label">
                Payment Date
              </label>
              <input
                type="date"
                id="paymentDate"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleInputChange}
                className="form-input"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="paymentReference" className="form-label">
                Payment Reference
              </label>
              <input
                type="text"
                id="paymentReference"
                name="paymentReference"
                value={formData.paymentReference}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Payment reference number"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="paymentTransactionId" className="form-label">
                Transaction ID
              </label>
              <input
                type="text"
                id="paymentTransactionId"
                name="paymentTransactionId"
                value={formData.paymentTransactionId}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Transaction ID"
                disabled={isView}
              />
            </div>
          </div>

          {/* Bank Account Information Section */}
          <div className="form-section-divider">
            <div>
              <h2 className="section-title">Bank Account Information</h2>
              <p className="section-subtitle">Employee bank account details for advance payment</p>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="bankAccountNumber" className="form-label">
                Account Number
              </label>
              <input
                type="text"
                id="bankAccountNumber"
                name="bankAccountNumber"
                value={formData.bankAccountNumber}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Bank account number"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="bankName" className="form-label">
                Bank Name
              </label>
              <input
                type="text"
                id="bankName"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Bank name"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ifscCode" className="form-label">
                IFSC Code
              </label>
              <input
                type="text"
                id="ifscCode"
                name="ifscCode"
                value={formData.ifscCode}
                onChange={handleInputChange}
                className="form-input"
                placeholder="IFSC code"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="accountHolderName" className="form-label">
                Account Holder Name
              </label>
              <input
                type="text"
                id="accountHolderName"
                name="accountHolderName"
                value={formData.accountHolderName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Account holder name"
                disabled={isView}
              />
            </div>
          </div>

          {/* Project/Client Information Section */}
          <div className="form-section-divider">
            <div>
              <h2 className="section-title">Project/Client Information (Optional)</h2>
              <p className="section-subtitle">Link this advance to a project or client</p>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="projectCode" className="form-label">
                Project Code
              </label>
              <input
                type="text"
                id="projectCode"
                name="projectCode"
                value={formData.projectCode}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Project code"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="projectName" className="form-label">
                Project Name
              </label>
              <input
                type="text"
                id="projectName"
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Project name"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="clientCode" className="form-label">
                Client Code
              </label>
              <input
                type="text"
                id="clientCode"
                name="clientCode"
                value={formData.clientCode}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Client code"
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="clientName" className="form-label">
                Client Name
              </label>
              <input
                type="text"
                id="clientName"
                name="clientName"
                value={formData.clientName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Client name"
                disabled={isView}
              />
            </div>
          </div>

          {/* Notes Section */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="form-input"
              rows="3"
              placeholder="Additional notes or comments"
              disabled={isView}
            />
          </div>

          {/* Form Actions */}
          {!isView && (
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleReset}>
                <RotateCcw size={16} />
                Reset
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading}>
                <Save size={16} />
                {loading ? 'Saving...' : isEdit ? 'Update' : 'Save'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default AdvancePaymentForm

